import { useState } from 'react';
import { useT } from '../../i18n/i18n.tsx';
import { checkVenuePasscode } from '../../api/session.ts';
import { Button, Dialog, Kbd, TextField } from '../../ui';
import { DEFAULT_HOTKEYS, splitBinding } from './hotkeys.ts';
import { useHotkeys } from './useHotkeys.ts';
import './StorefrontModeBar.css';

/**
 * MVP-500 Commit A. The indicator for "โหมดหน้าร้าน", and the way back out of it.
 *
 * === WHY THIS IS ITS OWN FILE ===
 * `lib/fullscreen.test.ts` asserts that `App.tsx` contains no case-insensitive match for
 * `fullscreen` anywhere — identifier, string or comment. That gate IS the ESC guarantee: the shell
 * cannot get the browser's whole-document state wrong because the shell has never heard of it. So
 * the rationale for this strip — which is entirely about that state — is written here instead, and
 * `App.tsx` gets one `<StorefrontModeBar />` line and an import.
 *
 * The rationale: storefront mode is NOT bound to fullscreen (see `storefrontMode.ts`'s header for
 * the full argument and the owner's agreement). F11 and ESC move the browser without the app being
 * asked, so a mode that followed them would silently rebind the KJ's keys with nothing on screen to
 * say so. This strip is the "something on screen": it is rendered in `App.tsx` as a sibling of the
 * outage strip, which `App.css`'s chrome-hiding rule deliberately excludes, so the KJ can still see
 * which mode the machine is in — and still leave it — with the header hidden.
 *
 * The exit control lives in THIS strip and not in `AppHeader`, for the same reason. `.app-header`
 * is `display: none` while the browser is showing only the document; a toggle that lived only there
 * would be unreachable at exactly the moment someone needs out. Entering is a header button
 * (`App.tsx`) because entering is only ever done with the chrome visible; leaving must work either
 * way, and this mode has no keyboard accelerator at all — the way in is a button and the way out is
 * a button.
 *
 * `role="status"`, not `alert`: nothing interrupted anyone, this is a state the operator chose.
 * Same choice and the same wording as the outage strip beside it.
 *
 * `ui/Button` rather than a bare `<button>`: it inherits the `@media (pointer: coarse)` control
 * height raise `styles/pointerTouchTargets.test.ts` pins, so the ≥44px touch floor on a tablet
 * comes free with no raw px in this component's stylesheet.
 */
export function StorefrontModeBar({ active, onExit }: { active: boolean; onExit: () => void }) {
  const t = useT();
  /**
   * MVP-678. The way out, behind the venue passcode — the owner's answer to "การที่จะเข้า/ออก
   * โหมดหน้าร้าน ควรต้องกรอก password ด้วยมั้ย": yes on the way OUT, on the passcode the venue
   * already has, and nothing on the way in (the KJ enters the mode with the chrome visible, where a
   * password stops nobody).
   *
   * ONE PROBE FIRST, ALWAYS. Pressing exit asks the server with no PIN at all. A venue that has not
   * set one answers `required: false` and the console leaves immediately — no dialog, no new
   * friction for the rooms this was never about. Only a venue that HAS one ever sees the prompt.
   *
   * === WHERE THIS FAILS OPEN, AND WHERE IT MUST NOT ===
   * `storefrontMode.ts`: "a reduced console nobody can get out of is a worse night than one that
   * never engaged, because the way out is itself one of the things the mode takes away." So a probe
   * that cannot be answered — the network is down, the backend is out, the session ended — LETS THE
   * OPERATOR OUT. The mode is a speed bump against whoever wanders up to the machine, not a vault,
   * and trading a stuck console for that is the wrong trade.
   *
   * A submitted PIN is the opposite: once the server has said `required: true`, a failing check
   * keeps the door shut. Otherwise the gate would be opened by pulling the network cable, and the
   * rate limit that makes a 4-digit PIN worth having (3 wrong answers per operator per 5 minutes)
   * would become the way through rather than the wall — exhaust it and every later attempt errors
   * out into "let them in". Signing out still clears the mode; it is per-machine `localStorage`,
   * not session state.
   */
  const [prompting, setPrompting] = useState(false);
  const [pin, setPin] = useState('');
  const [checking, setChecking] = useState(false);
  const [wrong, setWrong] = useState(false);

  function leave() {
    setPrompting(false);
    setPin('');
    setWrong(false);
    onExit();
  }

  async function requestExit() {
    // MVP-681 follow-up: the chord is still live while the prompt is up (the dialog traps focus but
    // `useHotkeys` listens on the document), and re-entering here would clear the PIN under
    // somebody's fingers — including the very digits they pressed Ctrl+Shift+X to be asked for.
    if (prompting || checking) return;
    setChecking(true);
    try {
      const verdict = await checkVenuePasscode(null);
      if (!verdict.required) return leave();
      setWrong(false);
      setPin('');
      setPrompting(true);
    } catch {
      leave(); // unanswerable probe — see the fail-open note above
    } finally {
      setChecking(false);
    }
  }

  async function submitPin() {
    setChecking(true);
    try {
      const verdict = await checkVenuePasscode(pin);
      if (verdict.ok) return leave();
      setWrong(true);
    } catch {
      // Rate-limited, or the network went away mid-attempt. The door stays shut; `wrong` is the
      // only sentence this strip has room for and it is the true one either way — this PIN did not
      // open it.
      setWrong(true);
    } finally {
      setChecking(false);
    }
  }

  /* MVP-679. The key is registered HERE, in the component that owns the exit, and it is live only
     while the mode is — `enabled` is `active`, so nothing listens for this chord on a machine that
     never turned the mode on.
     `DEFAULT_HOTKEYS` and not the operator's remapped table: this component is rendered by the
     shell, which has no settings to read, and the chord is deliberately identical in both tables
     (see `extremePreset.ts`) so there is nothing to resolve. If remapping this one is ever asked
     for, the table comes in as a prop and the shortcut sheet already reads the same source. */
  useHotkeys({ exitStorefront: () => void requestExit() }, DEFAULT_HOTKEYS, active);
  if (!active) return null;
  return (
    <div className="storefront-bar" role="status" data-testid="storefront-bar">
      <strong className="storefront-bar__title">{t('ops.storefront.active')}</strong>
      {/* MVP-679, owner: "แจ้งคีย์ลัดใน toast เลย". The chord is on the toast rather than only in
          the shortcut sheet, because the sheet is itself one of the things a reduced console is
          hard to go looking through — and this is the one shortcut whose whole job is to be found
          by someone who is stuck. `aria-hidden` on the caps: the button beside them already says
          what this does, and a screen reader reading "Control Shift X" between the sentence and
          the button would be noise. */}
      <span className="storefront-bar__hint text-meta" aria-hidden="true">
        {splitBinding(DEFAULT_HOTKEYS.exitStorefront).map((key) => (
          <Kbd key={key}>{key}</Kbd>
        ))}
      </span>
      <Button variant="secondary" size="sm" onClick={() => void requestExit()} disabled={checking}>
        {t('ops.storefront.exit')}
      </Button>
      {/* The prompt is a Dialog and not an inline field on the toast: the toast is deliberately
          small and half-transparent (MVP-679), which is the wrong place to type a credential and
          read a refusal. A dialog also traps focus, so the keyboard cannot wander off into the
          reduced console behind it mid-attempt. */}
      <Dialog
        open={prompting}
        onClose={() => setPrompting(false)}
        title={t('ops.storefront.passcodeTitle')}
        closeLabel={t('common.close')}
      >
        <form
          className="storefront-bar__gate"
          onSubmit={(e) => {
            e.preventDefault();
            void submitPin();
          }}
        >
          <TextField
            label={t('ops.storefront.passcodeLabel')}
            value={pin}
            maxLength={12}
            autoFocus
            error={wrong ? t('ops.storefront.passcodeWrong') : undefined}
            onChange={(e) => setPin(e.currentTarget.value)}
          />
          <Button type="submit" disabled={checking || pin.trim().length === 0}>
            {t('ops.storefront.exit')}
          </Button>
        </form>
      </Dialog>
    </div>
  );
}
